// preview 專用：書單展示「精緻版」。
// 串接方式與原本 Library/views/new_arrivals_list_show 完全相同
// （useCRUDController('/library/api/frontend/newbib_display')，欄位 bib_cover_images/bibitem_isbn/bib_title/bib_author/biblionumber），
// 只改「呈現樣式」：多組書單用膠囊切換器（省縱向、可擴充） + 精緻書卡橫向書架。
// 不動共用元件 → 舊頁維持原樣。
import { useEffect, useState } from "react";
import { useCRUDController, ImageFileId } from "../../ui/ot";
import { BookOpen, User, ChevronRight } from "lucide-react";
import dayjs from "dayjs";

const BookCard = ({ book }) => {
  const coverFileId = (book?.bib_cover_images || [])[0];
  const isbnSrc = book?.bibitem_isbn
    ? `https://media.taaze.tw/showLargeImageByIsbn.ashx?isbn=${book.bibitem_isbn}`
    : null;
  const open = () =>
    window.open(
      `/library/online_public_catalog_query_construction/collection_search/?biblionumber=${book?.biblionumber}`,
      "_blank"
    );

  return (
    <div style={{ flex: "0 0 auto", width: "150px", cursor: "pointer" }} onClick={open}>
      <div
        className="group"
        style={{
          position: "relative",
          height: "210px",
          borderRadius: "10px",
          overflow: "hidden",
          background: "#fff",
          boxShadow: "0 5px 16px rgba(30,58,138,0.18)",
          transition: "transform 0.3s ease, box-shadow 0.3s ease",
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = "translateY(-6px)";
          e.currentTarget.style.boxShadow = "0 12px 26px rgba(30,58,138,0.30)";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 5px 16px rgba(30,58,138,0.18)";
        }}
      >
        {coverFileId ? (
          <ImageFileId file_id={coverFileId} customize={true} className="w-full h-full object-contain" />
        ) : isbnSrc ? (
          <img
            src={isbnSrc}
            alt={book?.bib_title}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "linear-gradient(135deg,#1e3a8a,#3b82f6)",
              color: "#fff",
              padding: "14px",
              textAlign: "center",
              fontWeight: 600,
            }}
          >
            {book?.bib_title || "書目"}
          </div>
        )}
        {/* hover 查看詳情 */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(to top, rgba(15,23,42,0.82), transparent 55%)",
            opacity: 0,
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            paddingBottom: "12px",
            transition: "opacity 0.3s ease",
          }}
          onMouseOver={(e) => (e.currentTarget.style.opacity = 1)}
          onMouseOut={(e) => (e.currentTarget.style.opacity = 0)}
        >
          <span style={{ color: "#fff", fontWeight: 600, fontSize: "0.85rem", border: "1px solid #ffffffaa", borderRadius: "999px", padding: "4px 14px" }}>
            查看詳情
          </span>
        </div>
      </div>
      <div style={{ marginTop: "8px" }}>
        <div
          style={{
            fontWeight: 600,
            color: "#1e293b",
            fontSize: "0.88rem",
            lineHeight: 1.35,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {book?.bib_title}
        </div>
        {book?.bib_author && (
          <div style={{ color: "#64748b", fontSize: "0.78rem", marginTop: "2px", display: "flex", alignItems: "center", gap: "3px" }}>
            <User size={11} /> <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{book.bib_author}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const BookListsV2 = () => {
  const ctrl = useCRUDController({ apiRoute: "/library/api/frontend/newbib_display" });
  const [active, setActive] = useState(0);

  useEffect(() => {
    ctrl.getData({ release_date: dayjs().format("YYYY-MM-DD") });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lists = ctrl?.ResponseData || [];
  if (!lists.length) return null;
  const current = lists[Math.min(active, lists.length - 1)] || lists[0];
  const books = current?.bibs_list_items_data || [];

  return (
    <div style={{ background: "linear-gradient(135deg,#eff6ff,#e0e7ff)", borderRadius: "16px", padding: "1.25rem" }}>
      {/* 多組書單：膠囊切換器 */}
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "1.1rem" }}>
        {lists.map((t, i) => {
          const on = i === active;
          return (
            <button
              key={t?.list_id || i}
              type="button"
              onClick={() => setActive(i)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "0.5rem 1rem",
                borderRadius: "999px",
                border: on ? "none" : "1px solid #c7d2fe",
                background: on ? "linear-gradient(90deg,#1e3a8a,#2563eb)" : "#fff",
                color: on ? "#fff" : "#1e3a8a",
                fontWeight: 600,
                fontSize: "0.9rem",
                cursor: "pointer",
                boxShadow: on ? "0 3px 10px rgba(37,99,235,0.35)" : "none",
                transition: "all 0.2s ease",
              }}
            >
              <BookOpen size={15} />
              {t?.task_name}
              <span
                style={{
                  background: on ? "rgba(255,255,255,0.25)" : "#eef2ff",
                  color: on ? "#fff" : "#4f46e5",
                  borderRadius: "999px",
                  padding: "0 8px",
                  fontSize: "0.78rem",
                }}
              >
                {(t?.bibs_list_items_data || []).length} 本
              </span>
            </button>
          );
        })}
      </div>

      {/* 選中書單的說明 + 查看全部 */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
        <span style={{ color: "#475569", fontSize: "0.9rem" }}>{current?.task_explaination}</span>
        <span
          style={{ display: "inline-flex", alignItems: "center", gap: "2px", color: "#2563eb", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer" }}
          onClick={() => window.open("/library/online_public_catalog_query_construction/display_list", "_blank")}
        >
          查看全部 <ChevronRight size={15} />
        </span>
      </div>

      {/* 精緻書架（橫向捲動） */}
      <div style={{ display: "flex", gap: "18px", overflowX: "auto", padding: "0.5rem 0.25rem 1rem" }}>
        {books.map((book, i) => (
          <BookCard key={book?.biblionumber || i} book={book} />
        ))}
      </div>
    </div>
  );
};

export default BookListsV2;
